import { inject, injectable } from 'inversify'
import { get, isNil } from 'lodash-es'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  type DependencyToPreload,
  type FilterDependencyProvider,
  type FilterToFormattedTextConvertable,
  type FormattedTextBadgesValueItem,
  type FormattedTextNode,
  FilterOperationEnum,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TranslateService from '@/services/translateService'
import type UserService from '@/services/domain/user/userService'
import type { FilterToCreate } from '@/services/tables/filters/types'
import User from '@/data/models/domain/User'

@injectable()
export default class UserFilterMapper implements FilterToFormattedTextConvertable, FilterDependencyProvider {
  constructor(
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
  ) {}

  public toFormattedText(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): FormattedTextNode[] {
    const ids = this.getUserIds(filterValue, relatedFieldName)
    const hasEmptyUser = ids.includes('0')

    const result: FormattedTextNode[] = []
    if (hasEmptyUser) {
      result.push({
        type: 'italic',
        value: this.translateService.t('fieldAssignee.unassigned'),
      })
    }
    const badges: FormattedTextBadgesValueItem[] = ids
      .filter((id) => id !== '0')
      .map((id) => {
        return {
          name: this.userService.findEntityByIdOrNull(id)?.fullName || id,
        }
      })
    if (badges.length > 0) {
      result.push({
        type: 'badges',
        value: badges,
      })
    }
    return result
  }

  public getFilterDependencies(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): DependencyToPreload[] {
    return [
      {
        model: User,
        ids: this.getUserIds(filterValue, relatedFieldName).filter((id) => id && id !== '0'),
      },
    ]
  }

  protected getUserIds(filterValue: PaginationUrlFilterScalarValueType, relatedFieldName: string): Array<string | '0'> {
    const ids = get(filterValue, [relatedFieldName, FilterOperationEnum.in], []) as Array<string | number>

    return ids.filter((id) => !isNil(id)).map((id) => String(id))
  }
}
